import { apiClient, HttpError } from '@littlebird/auth-client';
import {
  keepPreviousData,
  MutationObserver,
  useIsMutating,
  useMutation,
  useQuery,
  type MutationObserverOptions,
} from '@tanstack/react-query';
import { useRef } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { queryClient } from '@/query/client';
import { trackEvent } from '@/utils/analytics';

import { accountConflict } from '../edits/edits.query';
import {
  invalidatePeople,
  invalidatePeopleList,
  parseWikiPeople,
  PEOPLE_STALE_TIME_MS,
  personDetailSchema,
  personQueryOptions,
  personWriteScope,
  toWikiPersonDetail,
} from '../people-wiki.query';

import {
  daysUntil,
  formatDays,
  sendingOf,
  type AddressEntry,
} from './connection-actions';

import type {
  WikiConnection,
  WikiPerson,
  WikiPersonDetail,
} from '../people-wiki.types';

/** Where the user started a connection, for analytics */
export type ConnectionSurface =
  | 'wiki_list'
  | 'wiki_requests'
  | 'share_picker'
  | 'composer_card';

type ConnectionPerson = Pick<WikiPerson, 'id' | 'name'>;

// The most addresses that the server looks up at once
const MAX_LOOKUP_EMAILS = 50;

/** How long what the user types has to stand still before the addresses it brings up are looked up, because each of
 * them counts against the daily limit of lookups */
export const LOOKUP_DELAY_MS = 400;

// A connect can send an email, so a failed attempt must not be repeated on its own
const NO_RETRIES = { retryOptions: { retries: 0 } };

const LITTLEBIRD_ACCOUNTS_KEY = ['people-wiki', 'littlebird-accounts'];

/** The key of every change to a connection, so that what shows them can tell that one is under way */
const CONNECTION_MUTATION_KEY = ['people-wiki', 'connection'];

const PENDING_CONNECTIONS_KEY = [
  'people-wiki',
  'people',
  { connection: 'pending' },
];

const littlebirdAccountsSchema = z.object({ emails: z.array(z.string()) });

const createdPersonSchema = z.object({ id: z.string(), name: z.string() });

const refusalSchema = z.object({
  detail: z.object({ reason: z.string(), resend_at: z.string().optional() }),
});

const connectionsUrl = (personId: string) =>
  `/people-wiki/people/${personId}/connections`;

/** The pages that hold a request or an invite with no answer yet, the most recently sent first. The key sits under
 * the key of the list, so a refresh of the people refreshes these too. */
export const usePendingConnectionsQuery = () =>
  useQuery(
    {
      queryKey: PENDING_CONNECTIONS_KEY,
      staleTime: PEOPLE_STALE_TIME_MS,
      refetchOnWindowFocus: false,
      queryFn: async ({ signal }) =>
        parseWikiPeople(
          await apiClient.get('/people-wiki/people', {
            signal,
            params: { connection: 'pending' },
          })
        ),
    },
    queryClient
  );

/** The answer of a lookup, with the addresses that it was asked about. An address that is not among them has not been
 * answered for, which matters when an answer stays on screen while a lookup of more addresses runs. */
export type LittlebirdAccountsAnswer = { asked: string[]; emails: string[] };

/** Which of the addresses are the sign-in email of a Littlebird user, and which addresses the answer is for. An answer
 * goes out of date when someone signs up, so it is read again on mount once it is stale, and after every change to a
 * connection. With `keepPrevious`, the answer for the addresses before stays until the one for a changed set comes,
 * and then `asked` tells which addresses it covers. */
export const useLittlebirdAccountsQuery = (
  emails: string[],
  { keepPrevious = false }: { keepPrevious?: boolean } = {}
) => {
  const sortedEmails = [...emails].sort();
  return useQuery(
    {
      queryKey: [...LITTLEBIRD_ACCOUNTS_KEY, sortedEmails],
      enabled: sortedEmails.length > 0,
      staleTime: PEOPLE_STALE_TIME_MS,
      refetchOnWindowFocus: false,
      retry: false,
      placeholderData: keepPrevious ? keepPreviousData : undefined,
      queryFn: async ({ signal }): Promise<LittlebirdAccountsAnswer> => {
        const asked = sortedEmails.slice(0, MAX_LOOKUP_EMAILS);
        const response = await apiClient.post(
          '/people-wiki/littlebird-accounts',
          { emails: asked },
          { signal }
        );
        return {
          asked,
          emails: littlebirdAccountsSchema.parse(response).emails,
        };
      },
    },
    queryClient
  );
};

/** Whether a connection is being changed, from a page or from an address. The change is under way until the lists have
 * been read again. */
export const useIsConnecting = (): boolean =>
  useIsMutating({ mutationKey: CONNECTION_MUTATION_KEY }, queryClient) > 0;

/** A route answered 404: the page is gone, or an account on it is */
const isNotFoundError = (error: unknown): boolean =>
  error instanceof HttpError && error.status === 404;

/** The page of the person is gone: a route answered 404 for the page, or for an account on it while a read of the page
 * answered 404 as well */
export class PageGoneError extends Error {
  constructor() {
    super('Person not found');
    this.name = 'PageGoneError';
  }
}

/** Whether a read of the page finds it gone. The read goes around the query cache, where a push that the page changed
 * or another write of the page would cancel it, and only a 404 says that the page is gone. */
const isPageGone = async (personId: string): Promise<boolean> => {
  try {
    await apiClient.get(`/people-wiki/people/${personId}`);
    return false;
  } catch (error) {
    return isNotFoundError(error);
  }
};

/** Why the server turned a request down, when it said so */
const refusalOf = (error: unknown) => {
  if (!(error instanceof HttpError)) return null;
  const refusal = refusalSchema.safeParse(error.body);
  return refusal.success ? refusal.data.detail : null;
};

const FALLBACK_MESSAGE = "Couldn't update this connection. Try again.";
const SELF_MESSAGE = "That's your own address";
const NOT_AN_EMAIL_MESSAGE = "That doesn't look like an email address";
const ADDRESS_CHANGED_MESSAGE = 'This address just changed. Try again.';
const PAGE_GONE_MESSAGE = 'This person is no longer in your wiki';
const REQUEST_GONE_MESSAGE = 'This request is no longer there';
const CONNECTION_GONE_MESSAGE = 'This connection is no longer there';
const NOT_ON_PAGE_MESSAGE = 'This address is no longer on their page';

/** What the user was trying to send, which the wait before the next send is told in */
type Sending = 'request' | 'invite';

/** The states of an account that a decline, a cancel or a remove acts on, as the user saw them */
export type DisconnectSeen = Exclude<WikiConnection, '' | 'invited'>;

const connectionErrorMessage = (
  error: unknown,
  name: string,
  sending: Sending
): string => {
  if (error instanceof HttpError && error.status === 502) {
    return "Couldn't send the email. Try again.";
  }
  const refusal = refusalOf(error);
  switch (refusal?.reason) {
    case 'too_soon':
      return refusal.resend_at
        ? `You can ${sending === 'invite' ? 'invite' : 'ask'} again in ${formatDays(daysUntil(refusal.resend_at))}`
        : FALLBACK_MESSAGE;
    case 'daily_limit':
      return "You've reached today's limit. Try again tomorrow.";
    case 'teammate':
      return `${name} is already on your team`;
    case 'same_person':
      return 'This is another of your own accounts';
    case 'not_available':
      return "Connections aren't available for this person yet";
    case 'connected':
      return `You're already connected with ${name}`;
    case 'self':
      return SELF_MESSAGE;
    case 'not_on_page':
      return NOT_ON_PAGE_MESSAGE;
    default:
      return FALLBACK_MESSAGE;
  }
};

/** The Wiki list, the pending connections and the account lookups, which a write on one page can change. The page
 * itself stays as the write left it, because the write answered with the page. */
export const refreshLists = () =>
  Promise.all([
    invalidatePeopleList(),
    queryClient.invalidateQueries({
      queryKey: PENDING_CONNECTIONS_KEY,
      exact: true,
    }),
    queryClient.invalidateQueries({ queryKey: LITTLEBIRD_ACCOUNTS_KEY }),
  ]);

// The refusals that tell the user acted on a page that is out of date, so the page is read again to show the state that
// is there. The wait before the next email is claimed before the rows are compared, so a click on an out-of-date page
// gets `too_soon` and not `changed`.
const STALE_PAGE_REASONS: ReadonlySet<string> = new Set([
  'too_soon',
  'not_on_page',
  'connected',
  'teammate',
  'same_person',
  'not_available',
  'self',
]);

/** Tells the user why a connection could not be changed, and reads again what went out of date. `lostMessage` is what
 * the user acted on when it turns out not to be there any more, or null when they need not be told. */
const reportConnectionError = async (
  error: unknown,
  person: ConnectionPerson,
  lostMessage: string | null,
  sending: Sending = 'request'
) => {
  const reason = refusalOf(error)?.reason;
  if (error instanceof PageGoneError) {
    toast.error(PAGE_GONE_MESSAGE);
    // A page that is gone has nothing to read again
    queryClient.removeQueries({
      queryKey: personQueryOptions(person.id).queryKey,
      exact: true,
    });
    await refreshLists();
  } else if (isNotFoundError(error) || reason === 'changed') {
    // The page is there, but the account is gone, or the rows are not as the user saw them, or another write got to
    // them first, such as a second click
    if (lostMessage) toast.error(lostMessage);
    await invalidatePeople();
  } else {
    toast.error(connectionErrorMessage(error, person.name, sending));
    if (reason && STALE_PAGE_REASONS.has(reason)) await invalidatePeople();
  }
};

/** Writes to one page go in turn, and a read of the page that is under way stops, so that it cannot land after the
 * write and bring back what the write changed */
const personWriteOptions = (personId: string) => ({
  scope: personWriteScope(personId),
  onMutate: () =>
    queryClient.cancelQueries({
      queryKey: personQueryOptions(personId).queryKey,
    }),
});

// What a decline, a cancel or a remove calls the thing that it found gone, by the state of the account that the user saw
const DISCONNECT_LOST_MESSAGES: Record<DisconnectSeen, string> = {
  incoming: REQUEST_GONE_MESSAGE,
  requested: REQUEST_GONE_MESSAGE,
  connected: CONNECTION_GONE_MESSAGE,
};

export type ConnectVariables = {
  /** An address on the page */
  email: string;
  /** The state of the Littlebird account of the address as the user sees it, which is blank when there is none */
  seen: WikiConnection;
  /** What the click sends, which tells a wait that is still running in the right words */
  sending: Sending;
  surface: ConnectionSurface;
};

/** Post the connection to an address of the page, and read the page that the server answers with */
const postConnection = async (
  personId: string,
  email: string,
  seen: WikiConnection
): Promise<WikiPersonDetail> => {
  try {
    const response = await apiClient.post(
      connectionsUrl(personId),
      { email, seen },
      NO_RETRIES
    );
    return toWikiPersonDetail(personDetailSchema.parse(response));
  } catch (error) {
    // The only thing that the route can fail to find is the page
    throw isNotFoundError(error) ? new PageGoneError() : error;
  }
};

/** Take in the page that a connection answered with, and track what went out */
const afterConnection = (
  detail: WikiPersonDetail,
  {
    email,
    seen,
    surface,
  }: Pick<ConnectVariables, 'email' | 'seen' | 'surface'>,
  { refresh }: { refresh: boolean } = { refresh: true }
) => {
  queryClient.setQueryData(personQueryOptions(detail.id).queryKey, detail);
  // What went out is what the row says, whatever the user clicked, and an accept sends nothing
  const connection = detail.littlebirdAccounts.find(
    (row) => row.email === email
  )?.connection;
  const resend = seen === 'invited';
  if (connection === 'requested') {
    trackEvent('PEOPLE_CONNECTION_REQUESTED', { surface, resend });
  } else if (connection === 'invited') {
    trackEvent('PEOPLE_INVITE_SENT', { surface, resend });
  }
  // The mutation stays pending until the lists catch up, so a second click cannot repeat the action
  return refresh ? refreshLists() : undefined;
};

/** Connect with the Littlebird user at an address of the person. The server decides what that means from the state of
 * the address: a request or an invite for the first time, an accept for a request that came in, and a resend for one
 * that was sent. It refuses when the state is not the one the user saw. */
const connectMutationOptions = (
  person: ConnectionPerson,
  { refresh }: { refresh: boolean }
): MutationObserverOptions<WikiPersonDetail, Error, ConnectVariables> => ({
  ...personWriteOptions(person.id),
  mutationKey: CONNECTION_MUTATION_KEY,
  mutationFn: ({ email, seen }) => postConnection(person.id, email, seen),
  onSuccess: (detail, variables) =>
    afterConnection(detail, variables, { refresh }),
  onError: (error, { seen, sending }) =>
    reportConnectionError(
      error,
      person,
      seen === 'incoming' ? REQUEST_GONE_MESSAGE : null,
      sending
    ),
});

export const useConnectMutation = (person: ConnectionPerson) =>
  useMutation(connectMutationOptions(person, { refresh: true }), queryClient);

/** What a click on an address sends, by the state that the address showed */
export const connectVariables = (
  { email, account, state }: AddressEntry,
  surface: ConnectionSurface
): ConnectVariables => ({
  email,
  seen: account?.connection ?? '',
  sending: sendingOf(state),
  surface,
});

/** The same send for a caller that connects with several people, where a hook for each person cannot be called. The
 * caller refreshes the lists once, after the last send. */
export const connectPerson = (
  person: ConnectionPerson,
  variables: ConnectVariables
) =>
  new MutationObserver(
    queryClient,
    connectMutationOptions(person, { refresh: false })
  ).mutate(variables);

/** The address that the user typed is on their own page, so there is no one to connect with */
class OwnAddressError extends Error {
  constructor() {
    super('Address of the user');
    this.name = 'OwnAddressError';
  }
}

const isUnprocessableError = (error: unknown): boolean =>
  error instanceof HttpError && error.status === 422;

/** The page that holds an address that the user typed, and the state of the Littlebird account of the address on it. A
 * new page has no such account. When a page holds the address already, it is read for the state that it has now, which
 * is the one that the connection is sent for. */
const pageForAddress = async (
  email: string
): Promise<{ page: ConnectionPerson; seen: WikiConnection }> => {
  try {
    const response = await apiClient.post(
      '/people-wiki/people',
      { name: email, emails: [email] },
      NO_RETRIES
    );
    return { page: createdPersonSchema.parse(response), seen: '' };
  } catch (error) {
    const existingPage = accountConflict(error);
    if (!existingPage) throw error;
    if (existingPage.isSelf) throw new OwnAddressError();
    const detail = await queryClient.fetchQuery({
      ...personQueryOptions(existingPage.personId),
      staleTime: 0,
    });
    const account = detail.littlebirdAccounts.find(
      (row) => row.email.toLowerCase() === email
    );
    return {
      page: { id: detail.id, name: detail.name },
      seen: account?.connection ?? '',
    };
  }
};

type ConnectAddressVariables = Pick<
  ConnectVariables,
  'email' | 'sending' | 'surface'
>;

/** Connect with the Littlebird user at an address that no page holds yet, such as one typed in the share picker. The
 * address gets its page first, and then the connection goes out as it does from any page. The writes to the page do
 * not wait their turn, as those of `useConnectMutation` do, because the page is new or was read a moment ago. */
export const useConnectAddressMutation = () => {
  // The page of the address once it has one, which a failure of the connection names
  const page = useRef<ConnectionPerson | null>(null);
  return useMutation(
    {
      mutationKey: CONNECTION_MUTATION_KEY,
      onMutate: () => {
        page.current = null;
      },
      mutationFn: async ({ email }: ConnectAddressVariables) => {
        const found = await pageForAddress(email);
        page.current = found.page;
        return {
          detail: await postConnection(found.page.id, email, found.seen),
          seen: found.seen,
        };
      },
      onSuccess: ({ detail, seen }, { email, surface }) =>
        afterConnection(detail, { email, seen, surface }),
      onError: async (error, { sending }) => {
        if (error instanceof OwnAddressError) {
          toast.error(SELF_MESSAGE);
        } else if (page.current) {
          // The page exists by now, so the list is read again to show it, whatever came of the connection
          await Promise.all([
            reportConnectionError(
              error,
              page.current,
              ADDRESS_CHANGED_MESSAGE,
              sending
            ),
            invalidatePeopleList(),
          ]);
        } else {
          toast.error(
            isUnprocessableError(error)
              ? NOT_AN_EMAIL_MESSAGE
              : FALLBACK_MESSAGE
          );
        }
      },
    },
    queryClient
  );
};

/** Decline a request that came in, cancel one that was sent, or remove a connection, by the state of the account. The
 * answer is empty when the page went away with its last account. The server refuses when the state is not the one the
 * user saw. */
export const useDisconnectMutation = (person: ConnectionPerson) =>
  useMutation(
    {
      ...personWriteOptions(person.id),
      mutationKey: CONNECTION_MUTATION_KEY,
      mutationFn: async ({
        accountId,
        seen,
      }: {
        accountId: string;
        seen: DisconnectSeen;
      }): Promise<WikiPersonDetail | null> => {
        let response: unknown;
        try {
          response = await apiClient.delete(
            `${connectionsUrl(person.id)}/${accountId}?${new URLSearchParams({ seen })}`
          );
        } catch (error) {
          // A 404 means that the page is gone, or only the account is, and a read of the page tells which
          throw isNotFoundError(error) && (await isPageGone(person.id))
            ? new PageGoneError()
            : error;
        }
        return response === undefined
          ? null
          : toWikiPersonDetail(personDetailSchema.parse(response));
      },
      onSuccess: (detail) => {
        const personKey = personQueryOptions(person.id).queryKey;
        if (detail) {
          queryClient.setQueryData(personKey, detail);
        } else {
          queryClient.removeQueries({ queryKey: personKey, exact: true });
        }
        return refreshLists();
      },
      onError: (error, { seen }) =>
        reportConnectionError(error, person, DISCONNECT_LOST_MESSAGES[seen]),
    },
    queryClient
  );
